import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import backendApi from '../shared/api/backendApi';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
});

const numberFormatter = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);
const formatNumber = (value) => numberFormatter.format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return 'No data';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

function MetricCard({ title, value, subtitle, icon: Icon, tone = 'indigo' }) {
  const toneStyles = {
    emerald: {
      borderColor: 'rgba(16, 185, 129, 0.35)',
      background: 'linear-gradient(145deg, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0.06) 100%)',
      valueColor: '#047857',
      iconBackground: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    indigo: {
      borderColor: 'rgba(99, 102, 241, 0.35)',
      background: 'linear-gradient(145deg, rgba(99, 102, 241, 0.18) 0%, rgba(99, 102, 241, 0.06) 100%)',
      valueColor: '#3730a3',
      iconBackground: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)'
    },
    orange: {
      borderColor: 'rgba(249, 115, 22, 0.35)',
      background: 'linear-gradient(145deg, rgba(249, 115, 22, 0.16) 0%, rgba(249, 115, 22, 0.05) 100%)',
      valueColor: '#c2410c',
      iconBackground: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
    },
    red: {
      borderColor: 'rgba(239, 68, 68, 0.35)',
      background: 'linear-gradient(145deg, rgba(239, 68, 68, 0.16) 0%, rgba(239, 68, 68, 0.05) 100%)',
      valueColor: '#b91c1c',
      iconBackground: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    }
  };

  const style = toneStyles[tone] || toneStyles.indigo;

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        borderColor: style.borderColor,
        background: style.background
      }}
    >
      <Box sx={{ p: 2.2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.25}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: style.valueColor, lineHeight: 1.2 }}>
              {value}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {Icon ? (
            <Avatar sx={{ width: 42, height: 42, background: style.iconBackground }}>
              <Icon sx={{ color: '#fff', fontSize: 22 }} />
            </Avatar>
          ) : null}
        </Stack>
      </Box>
    </Paper>
  );
}

function StatRow({ label, value, color = 'text.primary' }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.7, gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
    </Box>
  );
}

function InsightTile({ icon: Icon, title, value, subtitle }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.4,
        borderRadius: 2.5,
        borderColor: 'rgba(99, 102, 241, 0.2)',
        background: 'linear-gradient(145deg, rgba(99, 102, 241, 0.08) 0%, rgba(255, 255, 255, 0.94) 100%)'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(99, 102, 241, 0.16)', color: 'primary.main' }}>
          <Icon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
            {value || 'No data'}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function ReportsOverview() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [activeTab, setActiveTab] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = useCallback(async (year, month) => {
    try {
      setLoading(true);
      setError('');
      const response = await backendApi.getReportsOverview(year, month);
      setReport(response);
    } catch (err) {
      setError('Failed to load reports overview. Please try again.');
      console.error('Reports overview load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(selectedYear, selectedMonth);
  }, [loadReport, selectedMonth, selectedYear]);

  const yearOptions = useMemo(() => {
    const yearsFromApi = (report?.charts?.yearlyBreakdown || [])
      .map((entry) => entry.year)
      .filter((year) => Number.isInteger(year));
    if (yearsFromApi.length > 0) return [...new Set(yearsFromApi)].sort((a, b) => b - a);
    return Array.from({ length: 5 }, (_, index) => currentYear - index);
  }, [report, currentYear]);

  const monthLabel = report?.filters?.monthName || MONTHS.find((m) => m.value === selectedMonth)?.label;
  const monthly = report?.summary?.monthly || {};
  const yearly = report?.summary?.yearly || {};
  const inventory = report?.summary?.inventory || {};
  const insights = report?.insights || {};
  const monthlyBreakdown = report?.charts?.monthlyBreakdown || [];
  const yearlyBreakdown = report?.charts?.yearlyBreakdown || [];
  const topDealers = report?.charts?.topDealers || [];
  const topSellingProducts = report?.charts?.topSellingProducts || [];
  const dailySales = report?.charts?.dailySales || [];
  const maxDailySales = dailySales.reduce((max, row) => Math.max(max, Number(row.sales) || 0), 0);

  const monthlySales = Number(monthly.sales) || 0;
  const monthlyProfit = Number(monthly.profit) || 0;
  const yearlySales = Number(yearly.sales) || 0;
  const yearlyProfit = Number(yearly.profit) || 0;
  const monthlyMargin = monthlySales > 0 ? (monthlyProfit / monthlySales) * 100 : 0;
  const chartWidth = 1000;
  const chartHeight = 340;
  const chartPadding = 34;

  const trendChartData = useMemo(() => {
    const labels = monthlyBreakdown.length > 0
      ? monthlyBreakdown.map((row) => row.monthName)
      : MONTHS.map((month) => month.label.slice(0, 3));
    const salesValues = monthlyBreakdown.length > 0
      ? monthlyBreakdown.map((row) => Number(row.sales) || 0)
      : new Array(12).fill(0);
    const purchaseValues = monthlyBreakdown.length > 0
      ? monthlyBreakdown.map((row) => Number(row.purchases) || 0)
      : new Array(12).fill(0);
    const maxValue = Math.max(1, ...salesValues, ...purchaseValues);
    const count = salesValues.length;
    const step = count > 1 ? (chartWidth - chartPadding * 2) / (count - 1) : 0;

    const buildPoints = (values) =>
      values.map((value, index) => {
        const x = chartPadding + index * step;
        const y = chartHeight - chartPadding - (value / maxValue) * (chartHeight - chartPadding * 2);
        return { x, y };
      });

    const salesPoints = buildPoints(salesValues);
    const purchasesPoints = buildPoints(purchaseValues);
    const pathFromPoints = (points) => points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

    const salesPath = pathFromPoints(salesPoints);
    const purchasesPath = pathFromPoints(purchasesPoints);
    const salesAreaPath = salesPoints.length > 0
      ? `${salesPath} L ${salesPoints[salesPoints.length - 1].x} ${chartHeight - chartPadding} L ${salesPoints[0].x} ${chartHeight - chartPadding} Z`
      : '';

    return {
      labels,
      salesValues,
      purchaseValues,
      salesPoints,
      purchasesPoints,
      salesPath,
      purchasesPath,
      salesAreaPath,
      maxValue
    };
  }, [monthlyBreakdown]);

  const dealerChart = useMemo(() => {
    const colors = ['#8b5cf6', '#22c55e', '#f59e0b', '#38bdf8', '#f43f5e'];
    const top = topDealers.slice(0, 5);
    const total = top.reduce((sum, dealer) => sum + (Number(dealer.totalPurchase) || 0), 0);
    let start = 0;
    const segments = top.map((dealer, index) => {
      const value = Number(dealer.totalPurchase) || 0;
      const share = total > 0 ? (value / total) * 100 : 0;
      const end = start + share;
      const segment = {
        ...dealer,
        color: colors[index % colors.length],
        share
      };
      segment.gradient = `${segment.color} ${start}% ${end}%`;
      start = end;
      return segment;
    });
    const gradientParts = segments.map((segment) => segment.gradient);
    if (start < 100) gradientParts.push(`#e2e8f0 ${start}% 100%`);
    return {
      total,
      segments,
      gradient: gradientParts.length > 0 ? `conic-gradient(${gradientParts.join(', ')})` : 'conic-gradient(#e2e8f0 0% 100%)'
    };
  }, [topDealers]);

  if (loading && !report) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="360px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(99, 102, 241, 0.18)',
          background: 'linear-gradient(120deg, #0f172a 0%, #312e81 48%, #4338ca 100%)',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 260,
            height: 260,
            right: -90,
            top: -130,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 70%)'
          }}
        />
        <CardContent sx={{ p: { xs: 2.2, md: 3 }, position: 'relative', zIndex: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Typography variant="h4" sx={{ color: '#fff', mb: 0.8 }}>
                Reports Overview
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.88)' }}>
                Month-wise and year-wise analytics with cleaner visual focus on sales, profit, inventory, and top performers.
              </Typography>
              <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap sx={{ mt: 1.5 }}>
                <Paper sx={{ px: 1.2, py: 0.6, borderRadius: 99, bgcolor: 'rgba(255, 255, 255, 0.14)', color: '#fff' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Period: {monthLabel} {selectedYear}
                  </Typography>
                </Paper>
                <Paper sx={{ px: 1.2, py: 0.6, borderRadius: 99, bgcolor: 'rgba(255, 255, 255, 0.14)', color: '#fff' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Monthly Margin: {formatNumber(monthlyMargin)}%
                  </Typography>
                </Paper>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.13)',
                  border: '1px solid rgba(255, 255, 255, 0.24)',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1}>
                  <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                    <InputLabel id="reports-month-label" shrink sx={{ bgcolor: 'rgba(255,255,255,0.94)', px: 0.4, borderRadius: 1 }}>
                      Month
                    </InputLabel>
                    <Select
                      labelId="reports-month-label"
                      value={selectedMonth}
                      label="Month"
                      onChange={(event) => setSelectedMonth(parseInt(event.target.value, 10))}
                      disabled={loading}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.94)', borderRadius: 2 }}
                    >
                      {MONTHS.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                    <InputLabel id="reports-year-label" shrink sx={{ bgcolor: 'rgba(255,255,255,0.94)', px: 0.4, borderRadius: 1 }}>
                      Year
                    </InputLabel>
                    <Select
                      labelId="reports-year-label"
                      value={selectedYear}
                      label="Year"
                      onChange={(event) => setSelectedYear(parseInt(event.target.value, 10))}
                      disabled={loading}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.94)', borderRadius: 2 }}
                    >
                      {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={() => loadReport(selectedYear, selectedMonth)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    sx={{ minHeight: 40, whiteSpace: 'nowrap' }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && report ? <LinearProgress sx={{ mb: 2, borderRadius: 99 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Box
        sx={{
          mb: 2.5,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))'
          }
        }}
      >
        <Box>
          <MetricCard
            title={`Sales (${monthLabel})`}
            value={formatCurrency(monthlySales)}
            subtitle={`COGS ${formatCurrency(monthly.cogs)}`}
            icon={TrendingUpIcon}
            tone="emerald"
          />
        </Box>
        <Box>
          <MetricCard
            title={`Net Profit (${monthLabel})`}
            value={formatCurrency(monthlyProfit)}
            subtitle={`Expenses ${formatCurrency(monthly.expenses)}`}
            icon={MonetizationOnIcon}
            tone={monthlyProfit >= 0 ? 'indigo' : 'red'}
          />
        </Box>
        <Box>
          <MetricCard
            title={`Sales (${selectedYear})`}
            value={formatCurrency(yearlySales)}
            subtitle={`Net Profit ${formatCurrency(yearlyProfit)}`}
            icon={CalendarMonthIcon}
            tone="indigo"
          />
        </Box>
        <Box>
          <MetricCard
            title="Inventory Value"
            value={formatCurrency(inventory.valueAtSellingPrice)}
            subtitle={`${formatNumber(inventory.items)} items`}
            icon={Inventory2Icon}
            tone="orange"
          />
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          borderColor: 'rgba(15, 23, 42, 0.1)',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.94) 100%)'
        }}
      >
        <Box sx={{ px: 1.4, pt: 1.1 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 99,
                px: 2
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#fff',
                background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)'
              }
            }}
          >
            <Tab label="Overview" />
            <Tab label="Trends" />
            <Tab label="Leaders" />
          </Tabs>
        </Box>
        <Divider />

        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {activeTab === 0 ? (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(320px, 1fr)' },
                  mb: 2
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: 'rgba(99, 102, 241, 0.2)',
                    background: 'linear-gradient(145deg, #0b1025 0%, #141a3d 100%)',
                    color: '#fff',
                    minHeight: 420
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#fff' }}>
                    Monthly Revenue Trend ({selectedYear})
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
                    Sales and purchases trend overview
                  </Typography>
                  {monthlyBreakdown.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                      No monthly trend data available.
                    </Typography>
                  ) : (
                    <Box sx={{ height: { xs: 250, md: 310 }, border: '1px solid rgba(148,163,184,0.2)', borderRadius: 2, overflow: 'hidden' }}>
                      <svg
                        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                        preserveAspectRatio="xMidYMid meet"
                        style={{ width: '100%', height: '100%', display: 'block' }}
                      >
                        {[0, 1, 2, 3].map((row) => (
                          <line
                            key={row}
                            x1={chartPadding}
                            x2={chartWidth - chartPadding}
                            y1={chartPadding + ((chartHeight - chartPadding * 2) / 3) * row}
                            y2={chartPadding + ((chartHeight - chartPadding * 2) / 3) * row}
                            stroke="rgba(148,163,184,0.22)"
                          />
                        ))}
                        <path d={trendChartData.salesAreaPath} fill="rgba(168,85,247,0.16)" />
                        <path d={trendChartData.salesPath} fill="none" stroke="#a855f7" strokeWidth="3" />
                        <path d={trendChartData.purchasesPath} fill="none" stroke="#22c55e" strokeWidth="2.5" />
                        {trendChartData.salesPoints.map((point, index) => (
                          <g key={`sales-point-${index}`}>
                            <circle cx={point.x} cy={point.y} r="3.6" fill="#a855f7" stroke="#fff" strokeWidth="0.8" />
                            <circle cx={point.x} cy={point.y} r="11" fill="transparent">
                              <title>
                                {`${trendChartData.labels[index]}: Sales ${formatCurrency(trendChartData.salesValues[index])}`}
                              </title>
                            </circle>
                          </g>
                        ))}
                        {trendChartData.purchasesPoints.map((point, index) => (
                          <g key={`purchase-point-${index}`}>
                            <circle cx={point.x} cy={point.y} r="3.2" fill="#22c55e" stroke="#fff" strokeWidth="0.8" />
                            <circle cx={point.x} cy={point.y} r="11" fill="transparent">
                              <title>
                                {`${trendChartData.labels[index]}: Purchases ${formatCurrency(trendChartData.purchaseValues[index])}`}
                              </title>
                            </circle>
                          </g>
                        ))}
                        {trendChartData.salesPoints.map((point, index) => {
                          const showLabel = index % 2 === 0 || index + 1 === selectedMonth;
                          if (!showLabel) return null;
                          return (
                            <text
                              key={`x-label-${index}`}
                              x={point.x}
                              y={chartHeight - 8}
                              textAnchor="middle"
                              fontSize="14"
                              fill={index + 1 === selectedMonth ? '#fbbf24' : 'rgba(255,255,255,0.7)'}
                              fontWeight={index + 1 === selectedMonth ? '700' : '400'}
                            >
                              {trendChartData.labels[index]}
                            </text>
                          );
                        })}
                      </svg>
                    </Box>
                  )}
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: 'rgba(99, 102, 241, 0.2)',
                    background: 'linear-gradient(145deg, #111827 0%, #1f2937 100%)',
                    color: '#fff',
                    minHeight: 420,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#fff' }}>
                    Top Dealers ({monthLabel})
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
                    Purchase distribution
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.4 }}>
                    <Box sx={{ width: 165, height: 165, borderRadius: '50%', background: dealerChart.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: 102, height: 102, borderRadius: '50%', bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700 }}>{dealerChart.segments.length}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>dealers</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Stack spacing={0.95} sx={{ mt: 'auto' }}>
                    {dealerChart.segments.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        No dealer data available.
                      </Typography>
                    ) : (
                      dealerChart.segments.slice(0, 4).map((dealer, index) => (
                        <Box key={`${dealer.dealerId || 'dealer'}-${index}`}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="body2" sx={{ color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '72%' }}>
                              {dealer.dealerName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {formatNumber(dealer.share)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, dealer.share)}
                            sx={{
                              height: 7,
                              borderRadius: 99,
                              backgroundColor: 'rgba(148,163,184,0.2)',
                              '& .MuiLinearProgress-bar': { backgroundColor: dealer.color }
                            }}
                          />
                        </Box>
                      ))
                    )}
                  </Stack>
                </Paper>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(320px, 1fr)' }
                }}
              >
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.2 }}>
                    Key Insights
                  </Typography>
                  <Grid container spacing={1.2}>
                    <Grid item xs={12} sm={6}>
                      <InsightTile
                        icon={LocalFireDepartmentIcon}
                        title="Highest Sales Day"
                        value={formatDate(insights.highestSalesDayOfMonth?.date)}
                        subtitle={formatCurrency(insights.highestSalesDayOfMonth?.sales)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InsightTile
                        icon={TrendingUpIcon}
                        title="Highest Sales Month"
                        value={insights.highestSalesMonthOfYear?.monthName || 'No data'}
                        subtitle={formatCurrency(insights.highestSalesMonthOfYear?.sales)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InsightTile
                        icon={StarRoundedIcon}
                        title="Top Selling Product"
                        value={insights.topSellingProductThisMonth?.itemName || 'No data'}
                        subtitle={`Qty ${formatNumber(insights.topSellingProductThisMonth?.quantitySold)} | ${formatCurrency(insights.topSellingProductThisMonth?.salesRevenue)}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InsightTile
                        icon={PersonPinIcon}
                        title="Top Dealer + Product"
                        value={insights.topDealerMostBoughtProduct?.dealerName || insights.topDealer?.dealerName || 'No data'}
                        subtitle={`${insights.topDealerMostBoughtProduct?.itemName || 'No product data'} | Qty ${formatNumber(insights.topDealerMostBoughtProduct?.quantityBought)}`}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Quick Summary
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <StatRow label={`Monthly Sales (${monthLabel})`} value={formatCurrency(monthly.sales)} color="success.main" />
                  <StatRow label="Monthly Purchases" value={formatCurrency(monthly.purchases)} color="info.main" />
                  <StatRow label="Monthly Expenses" value={formatCurrency(monthly.expenses)} color="warning.main" />
                  <StatRow label="Monthly Net Profit" value={formatCurrency(monthlyProfit)} color={monthlyProfit >= 0 ? 'success.main' : 'error.main'} />
                  <Divider sx={{ my: 1 }} />
                  <StatRow label={`Yearly Sales (${selectedYear})`} value={formatCurrency(yearly.sales)} color="success.main" />
                  <StatRow label="Yearly Net Profit" value={formatCurrency(yearlyProfit)} color={yearlyProfit >= 0 ? 'success.main' : 'error.main'} />
                  <Divider sx={{ my: 1 }} />
                  <StatRow label="Inventory Items" value={formatNumber(inventory.items)} />
                  <StatRow label="Inventory Value (Sell)" value={formatCurrency(inventory.valueAtSellingPrice)} color="info.main" />
                </Paper>
              </Box>
            </Box>
          ) : null}

          {activeTab === 1 ? (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(320px, 1fr)' },
                  mb: 2
                }}
              >
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Month-wise Breakdown ({selectedYear})
                  </Typography>
                  <Divider sx={{ mb: 1.3 }} />
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Month</TableCell>
                          <TableCell align="right">Sales</TableCell>
                          <TableCell align="right">Purchases</TableCell>
                          <TableCell align="right">Expenses</TableCell>
                          <TableCell align="right">Net Profit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyBreakdown.map((row) => (
                          <TableRow key={row.month}>
                            <TableCell>{row.monthName}</TableCell>
                            <TableCell align="right">{formatCurrency(row.sales)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.purchases)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.expenses)}</TableCell>
                            <TableCell align="right" sx={{ color: Number(row.profit) >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                              {formatCurrency(row.profit)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, rgba(99, 102, 241, 0.08) 0%, rgba(255,255,255,0.95) 100%)'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Year-wise Performance
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Stack spacing={1}>
                    {yearlyBreakdown.map((row) => (
                      <Box key={row.year} sx={{ p: 1, borderRadius: 2, border: '1px solid rgba(99,102,241,0.16)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {row.year}
                          </Typography>
                          <Typography variant="caption" sx={{ color: Number(row.profit) >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                            {formatCurrency(row.profit)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Sales: {formatCurrency(row.sales)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Daily Sales ({monthLabel} {selectedYear})
                </Typography>
                <Divider sx={{ mb: 1.3 }} />
                {dailySales.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No daily sales data for this period.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.15,
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }
                    }}
                  >
                    {dailySales.map((row) => {
                      const sales = Number(row.sales) || 0;
                      const progress = maxDailySales > 0 ? (sales / maxDailySales) * 100 : 0;
                      return (
                        <Box key={`${row.date}-${sales}`} sx={{ p: 1.2, borderRadius: 2, border: '1px solid rgba(99,102,241,0.12)' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(row.date)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {formatCurrency(sales)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                              height: 7,
                              borderRadius: 99,
                              backgroundColor: 'rgba(99, 102, 241, 0.15)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 99,
                                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
                              }
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Box>
          ) : null}

          {activeTab === 2 ? (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  mb: 2
                }}
              >
                <MetricCard
                  title={`Top Product (${monthLabel})`}
                  value={insights.topSellingProductThisMonth?.itemName || 'No data'}
                  subtitle={`Qty ${formatNumber(insights.topSellingProductThisMonth?.quantitySold)} | Revenue ${formatCurrency(insights.topSellingProductThisMonth?.salesRevenue)}`}
                  icon={StarRoundedIcon}
                  tone="indigo"
                />
                <MetricCard
                  title={`Top Dealer (${monthLabel})`}
                  value={insights.topDealerMostBoughtProduct?.dealerName || insights.topDealer?.dealerName || 'No data'}
                  subtitle={`${insights.topDealerMostBoughtProduct?.itemName || 'No product data'} | Qty ${formatNumber(insights.topDealerMostBoughtProduct?.quantityBought)}`}
                  icon={PersonPinIcon}
                  tone="orange"
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
                }}
              >
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Top Selling Products ({monthLabel})
                  </Typography>
                  <Divider sx={{ mb: 1.3 }} />
                  <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Qty Sold</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topSellingProducts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No sales data for this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          topSellingProducts.map((row, index) => (
                            <TableRow key={`${row.itemId || 'item'}-${index}`}>
                              <TableCell>{row.itemName}</TableCell>
                              <TableCell align="right">{formatNumber(row.quantitySold)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.salesRevenue)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Top Dealers by Purchase ({monthLabel})
                  </Typography>
                  <Divider sx={{ mb: 1.3 }} />
                  <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Dealer</TableCell>
                          <TableCell align="right">Qty Bought</TableCell>
                          <TableCell align="right">Purchase Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topDealers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No purchase data for this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          topDealers.map((row, index) => (
                            <TableRow key={`${row.dealerId || 'dealer'}-${index}`}>
                              <TableCell>{row.dealerName}</TableCell>
                              <TableCell align="right">{formatNumber(row.totalQuantity)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.totalPurchase)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
}

export default ReportsOverview;
